import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { RiDeleteBinLine } from 'react-icons/ri'
import {
  ConfirmationModalLayout,
  ConfirmationModalShell,
} from '@/shared/components/confirmation/ConfirmationModalShell'
import {
  confirmationDangerButtonSx,
  confirmationHeadlineSx,
  confirmationSecondaryButtonSx,
} from '@/shared/components/confirmation/confirmationModalStyles'

interface DeactivateProps {
  open: boolean
  displayName: string
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function SignalProviderDeactivateModal({
  open,
  displayName,
  pending,
  onCancel,
  onConfirm,
}: DeactivateProps) {
  return (
    <ConfirmationModalShell
      open={open}
      onClose={pending ? undefined : onCancel}
      dialogTestId="signal-provider-deactivate-modal"
      ariaLabelledBy="signal-provider-deactivate-title"
    >
      <ConfirmationModalLayout>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#fff0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <RiDeleteBinLine size={24} color="#ef4444" aria-hidden />
        </Box>
        <Typography id="signal-provider-deactivate-title" sx={confirmationHeadlineSx}>
          Are you sure you want to deactivate {displayName}?
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, mb: 3 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={pending}
            data-testid="signal-provider-deactivate-cancel"
            sx={confirmationSecondaryButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={pending}
            data-testid="signal-provider-deactivate-confirm"
            sx={confirmationDangerButtonSx}
          >
            {pending ? <CircularProgress size={20} color="inherit" /> : 'Confirm Deactivate'}
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}

interface ReactivateProps {
  open: boolean
  displayName: string
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function SignalProviderReactivateModal({
  open,
  displayName,
  pending,
  onCancel,
  onConfirm,
}: ReactivateProps) {
  return (
    <ConfirmationModalShell
      open={open}
      onClose={pending ? undefined : onCancel}
      dialogTestId="signal-provider-reactivate-modal"
      ariaLabelledBy="signal-provider-reactivate-title"
    >
      <ConfirmationModalLayout>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#f0fdf4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: '#008236', fontSize: 28 }} />
        </Box>
        <Typography id="signal-provider-reactivate-title" sx={confirmationHeadlineSx}>
          Are you sure you want to reactivate {displayName}?
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, mb: 3 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={pending}
            data-testid="signal-provider-reactivate-cancel"
            sx={confirmationSecondaryButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={pending}
            data-testid="signal-provider-reactivate-confirm"
            sx={{
              ...confirmationDangerButtonSx,
              bgcolor: '#008236',
              '&:hover': { bgcolor: '#006b2d', boxShadow: 'none' },
            }}
          >
            {pending ? <CircularProgress size={20} color="inherit" /> : 'Confirm Reactivate'}
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}
